import { useEffect, useState } from "react";
import { Download, X, Share, SquarePlus } from "lucide-react";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent || "") ||
    // iPadOS 13+ reports as Mac but is touch-capable
    (/(mac)/i.test(navigator.userAgent || "") && "ontouchend" in document));

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true)) || false;

/* Global, always-available "Install App" pill. Auto-hides once the app is
   installed / running standalone. On Android/desktop Chrome it fires the real
   install prompt (captured beforeinstallprompt); on iOS (which never fires that
   event) it opens simple Share -> Add to Home Screen instructions. Dismissible
   per browser session so it never nags, but returns next visit until installed. */
export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;                                   // already installed
    if (sessionStorage.getItem("mist_install_dismissed")) return; // dismissed this session

    const onBIP = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    if (isIOS()) setShow(true);                                   // manual path for iOS

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* noop */ }
      setDeferred(null);
      setShow(false);
    } else {
      setIosHelp(true);                                           // iOS / no prompt available
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("mist_install_dismissed", "1");
    setShow(false);
  };

  return (
    <>
      <div
        data-testid="pwa-install-pill"
        className="absolute left-1/2 -translate-x-1/2 bottom-28 z-[70] flex items-center gap-2 rounded-full border-2 border-gold bg-[#141414] pl-3 pr-1.5 py-1.5 shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
      >
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="flex items-center gap-1.5 rounded-full bg-gold px-3 py-1.5 text-black active:scale-95"
        >
          <Download size={14} strokeWidth={2.6} />
          <span className="font-mono text-[10px] font-black uppercase tracking-widest">Install App</span>
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Not now"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 border-white/25 text-white/60 active:scale-95"
        >
          <X size={13} />
        </button>
      </div>

      {iosHelp && (
        <div
          data-testid="pwa-ios-help"
          className="absolute inset-0 z-[90] flex items-end bg-black/70"
          onClick={() => setIosHelp(false)}
        >
          <div
            className="w-full rounded-t-3xl border-t-4 border-gold bg-[#141414] p-5 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center gap-2">
              <Download size={16} className="text-gold" />
              <h3 className="flex-1 font-serif text-lg text-white">Add Misteck to your Home Screen</h3>
              <button onClick={() => setIosHelp(false)} data-testid="pwa-ios-close" className="grid h-8 w-8 place-items-center rounded-full border-2 border-white/25 text-white"><X size={15} /></button>
            </div>
            <ol className="space-y-3">
              <li className="flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 border-gold bg-black text-gold"><Share size={16} /></span>
                <span className="font-mono text-[12px] text-white/80">Tap the <span className="text-gold">Share</span> button in your browser's toolbar.</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 border-gold bg-black text-gold"><SquarePlus size={16} /></span>
                <span className="font-mono text-[12px] text-white/80">Choose <span className="text-gold">Add to Home Screen</span>.</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 border-gold bg-black font-mono text-[13px] font-black text-gold">✓</span>
                <span className="font-mono text-[12px] text-white/80">Tap <span className="text-gold">Add</span> — Misteck lands on your home screen like an app.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
